// Forecast display rules for the weather safety page — DOM-free, so they can be
// pinned down in forecast.test.js the way incidentMap.js is.
//
// The thresholds themselves are NOT here: they're data in the backend
// (app/safety/rules.py), served by GET /safety/config as `hazards`, and passed in.
// This module only applies them, and decides which forecast rows a window covers.
//
// The server applies the same rules to every fetch (app/safety/evaluate.py), for
// the refresh schedule and the change commentary; keep the two in step.

export const BAND_NONE = "none";
// "Approaching amber": past a metric's `watch` level but short of amber. It
// changes no decision; it's what tells "well below amber" from "just under it".
export const BAND_WATCH = "watch";
export const BAND_AMBER = "amber";
export const BAND_RED = "red";
const RANK = { [BAND_NONE]: 0, [BAND_WATCH]: 1, [BAND_AMBER]: 2, [BAND_RED]: 3 };

export const worseBand = (a, b) => (RANK[b] > RANK[a] ? b : a);

// A number, or null for anything that isn't one. Guards the `Number(null) === 0`
// trap: a missing reading must never be judged as a zero.
const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? null : Number(v));

// The band one metric puts one value in. "above" is at-or-above the threshold;
// "below" is at-or-below. A metric without a `watch` level has no watch band.
export function metricBand(metric, value) {
  const v = num(value);
  if (v === null) return BAND_NONE;
  const hit = (t) => t !== undefined && t !== null && (metric.direction === "below" ? v <= t : v >= t);
  if (hit(metric.red)) return BAND_RED;
  if (hit(metric.amber)) return BAND_AMBER;
  if (hit(metric.watch)) return BAND_WATCH;
  return BAND_NONE;
}

// Every hazard's band for one record: { wind: { band, metric, value }, … }.
// A hazard with several metrics takes the worst of them, and reports the metric
// that set it (so "Wind: red" can say "gust 58 mph").
export function recordBands(record, hazards) {
  const out = {};
  for (const h of hazards) {
    let best = { band: BAND_NONE, metric: null, value: null };
    for (const m of h.metrics) {
      const band = metricBand(m, record[m.field]);
      if (RANK[band] > RANK[best.band]) best = { band, metric: m, value: num(record[m.field]) };
    }
    out[h.key] = best;
  }
  return out;
}

const ms = (iso) => new Date(iso).getTime();
const HOUR = 3600 * 1000;

// Whether a record bears on the window. A record's period values cover the step
// that *ends* at its time (see providers/common.py), so a record counts when that
// period overlaps the window at all: an 08:00 three-hourly record (05:00-08:00)
// matters to a 07:00 start. The period is (t - step, t], so its end is
// inclusive: that's what lets a zero-width window — a start line, where the
// leaders and the back of the field leave at the same moment — still catch the
// step containing that instant.
export function inWindow(record, startIso, endIso) {
  const t = ms(record.time);
  const step = (record.step_hours || 1) * HOUR;
  return t >= ms(startIso) && t - step < ms(endIso);
}

export const windowRecords = (series, startIso, endIso) =>
  (series || []).filter(r => inWindow(r, startIso, endIso));

// The hours a point's forecast is judged over: its own exposure window when it
// sits on a route (leaders arrive → back of the field arrives), otherwise the
// assessment's whole window. Both ends or neither — half a window isn't one.
export function pointWindow(point, assessment) {
  if (point?.window_start && point?.window_end) {
    return { start: point.window_start, end: point.window_end, own: true };
  }
  return { start: assessment.window_start, end: assessment.window_end, own: false };
}

// Fields only the three-hourly product carries.
const THREE_HOURLY_ONLY = ["lightning_prob", "heavy_rain_prob"];

// One Met Office series from its two products: hourly rows where there are any
// (the first 48 hours), each borrowing the lightning and heavy-rain chance from
// the three-hourly step that contains it, then three-hourly rows beyond the last
// hourly one. Without the borrowing, the hourly view would show no lightning risk
// at all, which reads as "none" rather than "not in this product".
export function mergeMetOffice(hourly, threeHourly) {
  const h = hourly || [];
  const th = (threeHourly || []).slice().sort((a, b) => ms(a.time) - ms(b.time));
  const merged = h.map(r => {
    const t = ms(r.time);
    const step = th.find(s => ms(s.time) >= t && ms(s.time) - 3 * HOUR < t);
    if (!step) return r;
    const extra = {};
    for (const f of THREE_HOURLY_ONLY) if (step[f] !== undefined) extra[f] = step[f];
    return { ...r, ...extra };
  });
  const lastHourly = h.length ? Math.max(...h.map(r => ms(r.time))) : -Infinity;
  return [...merged, ...th.filter(s => ms(s.time) > lastHourly)];
}

// The worst of each hazard across a set of records, with when it happens:
// { wind: { band, metric, value, time }, … }. Ties go to the more extreme value
// (the higher gust, the colder feels-like), then the earlier time.
export function worstInRecords(records, hazards) {
  const out = {};
  for (const h of hazards) out[h.key] = { band: BAND_NONE, metric: null, value: null, time: null };
  for (const r of records) {
    const bands = recordBands(r, hazards);
    for (const h of hazards) {
      const cur = out[h.key];
      const b = bands[h.key];
      if (b.band === BAND_NONE) continue;
      const worse = RANK[b.band] > RANK[cur.band]
        || (b.band === cur.band && b.metric === cur.metric && b.metric
            && (b.metric.direction === "below" ? b.value < cur.value : b.value > cur.value));
      if (worse) out[h.key] = { ...b, time: r.time };
    }
  }
  return out;
}

// The latest forecast rows for one point, keyed by provider/product.
export function pointForecasts(forecasts, pointId) {
  const out = {};
  for (const f of forecasts || []) {
    if (f.point_id === pointId) out[`${f.provider}:${f.product}`] = f;
  }
  return out;
}

// The MWIS day whose date falls inside the window (a date string "YYYY-MM-DD"
// compared with the window's London-local dates). Several days can match a long
// window; all are returned in order.
export function mwisDaysInWindow(mwis, startIso, endIso) {
  const local = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Europe/London" });
  const from = local(startIso);
  const to = local(endIso);
  return (mwis?.days || []).filter(d => d.date && d.date >= from && d.date <= to);
}

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
                 "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
export const compass = (deg) => (num(deg) === null ? "" : COMPASS[Math.round(num(deg) / 22.5) % 16]);
